import ProxyButton from '../../components/common/ProxyButton';
import AmplitudeUtils from '../../utils/amplitude-utils';
import {
  TOTP_PROVIDER_AMPLITUDE_EVENT
} from '../../constants';
import BaseForgeCustomizer from '../base-forge';
import { isAnetUser } from '../../utils/user-utils';
import TotpAppsList from '../../components/enroll-totp/TotpAppsList';
import Header from '../../components/common/Header';
import Footer from '../../components/authenticator-app-enroll/Footer';
import BarcodeContainer from '../../components/authenticator-app-enroll/BarcodeContainer';
import { getLocalizedString } from '../../utils/i18n-utils';

export default class EnrollAuthenticatorCustomizer extends BaseForgeCustomizer {
  displayFooterText = isAnetUser();
  private amplitudeEventPrefix: string = TOTP_PROVIDER_AMPLITUDE_EVENT['GOOGLE'];

  async shouldCustomize() {
    return (
      this.context.formName === 'enroll-authenticator' &&
      this.context.authenticatorKey === 'google_otp'
    );
  }

  protected forgifyScanInstructionsText() {
    const scanInstructionsDetails: JQuery<HTMLElement> = jQueryCourage(
      '.qrcode-info-container .google-authenticator-setup-info'
    ).eq(0);
    this.addToHideList(scanInstructionsDetails.get(0));
  }

  private forgifyVerifyButton() {
    const oktaNextButton: JQuery<HTMLElement> = jQueryCourage('.google-authenticator-verify');
    if (oktaNextButton.length) {
      const forgeComponent = (
        <ProxyButton
          oktaButton={oktaNextButton}
          variant="primary"
          fullWidth
          size="large"
          className={'fe_u_margin--bottom-large'}
          onClick={() => {
            AmplitudeUtils.logMfaEnrollEvent(`${this.amplitudeEventPrefix}TotpActivatePush::VerifyButtonClick`);
            this.eventEmitter.emitEventToComponent({
              componentName: Header.displayName as string,
              eventName: 'update-header-description',
              eventPayload: { headerText: getLocalizedString('barcode.totp.manual.secret.key.instructions.text') },
            });
          }}
        />
      );
      this.renderForgeAndHideOktaComponent({
        id: oktaNextButton.attr('class')!,
        oktaComponent: oktaNextButton,
        forgeComponent: forgeComponent,
      });
    }
  }

  private forgifyNextButton() {
    const oktaNextButton: JQuery = jQueryCourage('.google-authenticator-next');
    if (oktaNextButton.length) {
      const forgeComponent = (
        <ProxyButton
          oktaButton={oktaNextButton}
          variant="primary"
          fullWidth
          size="large"
          className="fe_u_margin--bottom-large"
          onClick={() => {
            // The next button here just updates this view, and thus, to track this in a uniform way,
            // we log this as landing event to give an impression user moved to next screen
            AmplitudeUtils.logMfaEnrollEvent(`${this.amplitudeEventPrefix}TotpActivatePush::Landing`);
            this.eventEmitter.emitEventToComponent({
              componentName: Header.displayName!,
              eventName: 'update-header-title',
              eventPayload: { headerText: getLocalizedString('totp.enroll.step.2.header.text') },
            });
            this.eventEmitter.emitEventToComponent({
              componentName: Footer.displayName!,
              eventName: 'display-go-back',
              eventPayload: { show: true },
            });
          }}
        />
      );
      this.renderForgeAndHideOktaComponent({
        id: oktaNextButton.attr('class')!,
        oktaComponent: oktaNextButton,
        forgeComponent: forgeComponent,
      });
    }
  }

  protected forgifyBarcodeView() {
    this.forgifyBarCodeScanInstructions();
    this.forgifyScanInstructionsText();
    this.forgifyBarCodeContainer();
    this.forgifyNextButton();
  }

  private forgifyBarCodeContainer() {
    const oktaComponent = jQueryCourage('.qrcode-info-container');

    const forgeComponent = <BarcodeContainer oktaBarcodeContainer={oktaComponent} />;
    this.renderForgeAndHideOktaComponent({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: oktaComponent.attr('class')!,
      oktaComponent: oktaComponent,
      forgeComponent: forgeComponent,
    });
  }

  private forgifyBarCodeScanInstructions() {
    const oktaComponent = jQueryCourage('.barcode-setup-title');
    const forgeComponent = (
      <div className={'fe_u_text-align--left fe_u_color--muted'}>
        <p>{oktaComponent.text()}</p>
        <TotpAppsList />
      </div>
    );

    this.renderForgeAndHideOktaComponent({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: oktaComponent.attr('class')!,
      oktaComponent: oktaComponent,
      forgeComponent: forgeComponent,
    });
  }

  protected forgifyManualSetupView() {
    const oktaSecretKeyInstructions: JQuery<HTMLElement> = jQueryCourage(
      '.oie-enroll-google-authenticator-manual-setup .google-authenticator-setup-info'
    );
    this.addToHideList(oktaSecretKeyInstructions[0]); // this component gets forgified in InputFieldFactory
    this.forgifySubTitle('.manual-setup-title');
    this.forgifySubTitle('.enter-code-title');
    this.forgifyVerifyButton();
  }

  protected forgifyController() {
    AmplitudeUtils.logMfaEnrollEvent(`${this.amplitudeEventPrefix}TotpBarcodeScan::Landing`);
    this.onIdxProceed();
    this.forgifyBarcodeView();
    this.forgifyManualSetupView();
    super.forgifyController();
  }

  protected onIdxProceed() {
    const {
      oktaSignIn: { authClient },
    } = this;
    const originalProceed = authClient.idx.proceed.bind(authClient);
    authClient.idx.proceed = async (...args: any) => {
      const response = await originalProceed.apply(authClient, args);
      if (args[0].step === 'enroll-authenticator' && response.requestDidSucceed) {
        AmplitudeUtils.logMfaEnrollEvent(`${this.amplitudeEventPrefix}::Success`);
      }
      return response;
    };
  }

  protected getFooter({ footerLink }: { footerLink: JQuery }): React.JSX.Element {
    return <Footer text={this.footerText} footerLinks={footerLink?.not('[data-se=cancel]')} />;
  }
}
